import axios from 'axios';
import { createInterceptorToast } from './toastManager';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Variables to hold injected functions
let injectedLogoutFunction = () => { console.error('Logout function not injected into api.js'); };
let injectedToastFunction = (options) => { console.error('Toast function not injected into api.js', options); };

// Function to inject logout from a React component
export const setAuthAndNavigationFunctions = (logout) => {
  injectedLogoutFunction = logout;
};

// Function to inject toast from a React component (wrapped with deduplication)
export const setToastFunction = (toast) => {
  injectedToastFunction = createInterceptorToast(toast);
};

// Request Interceptor: Attach JWT token to every outgoing request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('jwt_token');
    // Only add the token if it exists and the Authorization header is not already set.
    // This prevents overwriting a header set for other purposes and avoids issues
    // with cookie-based authentication where no token is in sessionStorage.
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle various error cases
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with a status code that falls out of the range of 2xx
      const { status, data, config } = error.response;

      switch (status) {
        case 401:
          //console.log('[API-INTERCEPTOR] 🚨 401 Unauthorized at:', new Date().toISOString(), 'URL:', config.url);

          // List of public endpoints that should NOT trigger logout on 401
          const publicEndpoints = [
            '/api/auth/me',
            '/api/popups/active',
            '/api/catalogues/catalogue/products'
          ];

          // Check if this is a public endpoint
          const isPublicEndpoint = publicEndpoints.some(endpoint => config.url.includes(endpoint));

          if (isPublicEndpoint) {
            //console.log('[API-INTERCEPTOR] ⚠️ 401 on public endpoint, NOT triggering logout:', config.url);
            return Promise.reject(error);
          }

          // Check if this is a token expired response that needs refresh
          if (data.code === 'TOKEN_EXPIRED' && data.refreshRequired) {
            //console.log('[API-INTERCEPTOR] 🔄 Token expired, attempting refresh...');
            // Try to refresh the token automatically
            return axiosInstance.post('/api/auth/refresh')
              .then(() => {
                //console.log('[API-INTERCEPTOR] ✅ Token refreshed, retrying request');
                // Retry the original request after successful refresh
                return axiosInstance(config);
              })
              .catch(refreshError => {
                //console.log('[API-INTERCEPTOR] ❌ Token refresh failed, logging out');
                // If refresh fails, logout
                injectedLogoutFunction('Your session has expired. Please log in again.', '/login', { from: { pathname: window.location.pathname } });
                return Promise.reject(refreshError);
              });
          }

          // Handle 401 Unauthorized: Use injected logout function
          //console.log('[API-INTERCEPTOR] 🚪 Triggering LOGOUT due to 401 on:', config.url);
          injectedLogoutFunction('Your session has expired. Please log in again.', '/login', { from: { pathname: window.location.pathname } });
          break;
        case 403:
          // Handle 403 Forbidden
          injectedToastFunction({
            title: 'Access Denied',
            description: data.message || 'You do not have permission to access this resource.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          break;
        case 404:
          // Silently handle 404 - resource not found is often expected behavior
          //console.log(`Resource not found (404): ${config.method?.toUpperCase()} ${config.url}`);
          break;
        case 500:
        case 502:
        case 503:
          // Handle 5xx Server Errors (grouped by interceptor toast)
          injectedToastFunction({
            title: 'Server Error',
            description: 'An unexpected error occurred on the server. Please try again later.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          break;
        default:
          // Handle other HTTP errors (e.g., 400, 422, etc.)
          injectedToastFunction({
            title: 'Error',
            description: data.message || `An error occurred: ${status}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          break;
      }
    } else if (axios.isAxiosError(error) && !error.response) {
      // The request was made but no response was received (network error)
      injectedToastFunction({
        title: 'Network Error',
        description: 'Could not connect to the server. Please check your internet connection.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      injectedToastFunction({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;